import TextSnippetWrapper from 'components/TextSnippetWrapper';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React from 'react';
import { addTax } from 'utils/utility_functions/commonCalculations';
import { fixToTwoDecimal, formatNumbersToCurrencyString, validateNumericInput } from 'utils/utils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';

const FlatFeeBillingDetailsV2 = (props) => {
  const {
    caseDetails,
    addFlatFee,
    flatFees,
    flatFeeError,
    onChangeFlatFeeDate,
    flatFeeActualTotal,
    flatFeeTotal,
    setIsChanged,
    isChanged,
    teamMembers,
    setFlatFeeError,
    setFlatFees,
    deleteFlatFee,
    timezone,
  } = props || {};

  // Update by id using previous state so correct row is updated and new row stays below
  const updateRowById = (rowId, updates) => {
    if (rowId == null) return;
    setFlatFees((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...updates } : row)));
    isChanged.flat_fees = true;
    setIsChanged({ ...isChanged });
  };

  const nonBillableFlatFeeBody = (rowData, props) => {
    const rowId = rowData?.id;
    return (
      <>
        <span className="p-column-title text-break">Billable</span>
        <label className="switch">
          <input
            type="checkbox"
            checked={rowData.billable}
            name="non_billable"
            onChange={(e) => updateRowById(rowId, { billable: e.target.checked })}
          />{' '}
          <span className="slider round"></span>
        </label>
      </>
    );
  };

  return (
    <>
      <div>
        <div className="caption-bold mb-3 mt-3">Flat Fees</div>
        <div className="card-bg">
          <div className="row">
            <div className="col-12">
              <div className="datatable-responsive">
                <DataTable
                  className="p-datatable-responsive"
                  value={flatFees}
                  dataKey="id"
                  editMode="cell"
                  emptyMessage={() => <div>No records found</div>}
                >
                  <Column
                    header="Date"
                    field="date"
                    body={(rowData, props) => {
                      // Display the date without mutating rowData
                      const displayDate = rowData?.flat_fee_date
                        ? convertDateFromApiToUserTimezone(rowData.flat_fee_date, timezone, constants.month_date_year_format)
                        : '';
                      return (
                        <>
                          <span className="p-column-title text-break">Date</span>
                          <span className="text-break">{displayDate}</span>
                          <br></br>
                          {/* TODO:ERROR HANDLING */}
                          {flatFeeError && flatFeeError[`${props.rowIndex}_date`] && (
                            <span className="text-danger">{flatFeeError[`${props.rowIndex}_date`]}</span>
                          )}
                        </>
                      );
                    }}
                    editor={(props) => {
                      const rowId = props?.rowData?.id;
                      return (
                        <div className="table-input">
                          <TimezoneDatePicker
                            value={props.rowData?.flat_fee_date}
                            onChange={(value) => {
                              updateRowById(rowId, { flat_fee_date: value });
                              setFlatFeeError((prev) => {
                                const next = { ...prev };
                                delete next[`${props.rowIndex}_date`];
                                return next;
                              });
                              setIsChanged({ ...isChanged, flat_fees: true });
                            }}
                            className="w-100 border-0 p-2"
                            timezone={timezone}
                            isEndOfDay={true}
                          />
                          {flatFeeError && flatFeeError[`${props.rowIndex}_date`] && (
                            <span className="text-danger">{flatFeeError[`${props.rowIndex}_date`]}</span>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Column
                    header="Members"
                    field={'employee_name'}
                    body={(rowData) => {
                      return (
                        <>
                          <span className="p-column-title text-break">Members</span>
                          <span className="text-break">
                            {rowData?.employee_name && !rowData.employee_name.includes('undefined') ? (
                              rowData?.employee_name
                            ) : (
                              <i>Please Enter</i>
                            )}
                          </span>
                        </>
                      );
                    }}
                    editor={(props) => {
                      const rowId = props?.rowData?.id;
                      const selectedMember =
                        teamMembers?.find(
                          (m) => m.contact_id === props?.rowData?.employee_id || m.user_id === props?.rowData?.employee_id
                        ) ?? null;
                      return (
                        <Dropdown
                          options={teamMembers}
                          optionLabel="name"
                          value={selectedMember}
                          placeholder="Select"
                          onChange={(e) => {
                            const val = e.target.value;
                            updateRowById(rowId, {
                              employee_name: val?.name,
                              employee_id: val?.contact_id ?? val?.user_id,
                            });
                          }}
                          filter
                        />
                      );
                    }}
                  />
                  <Column
                    header="Item"
                    body={(rowData) => {
                      return (
                        <>
                          <span className="p-column-title text-break">Item</span>
                          <span className="text-break">{rowData.item_name}</span>
                        </>
                      );
                    }}
                  />
                  <Column
                    header="Flat Fee Notes"
                    field="notes"
                    body={(rowData) => {
                      return (
                        <>
                          <span className="p-column-title text-break">Flat Fee Notes</span>
                          <span className="text-break">{rowData.flat_fee_notes ? rowData.flat_fee_notes : <i>Please Enter</i>}</span>
                        </>
                      );
                    }}
                    editor={(props) => {
                      const rowId = props?.rowData?.id;
                      return (
                        <TextSnippetWrapper
                          type="textarea"
                          value={props.rowData?.flat_fee_notes}
                          onChange={(e) => updateRowById(rowId, { flat_fee_notes: e.target.value })}
                          className="w-100 input-shadow"
                          autoComplete="off"
                        />
                      );
                    }}
                    footer="Total"
                  />
                  <Column
                    header="Amount"
                    field="flat_fee_amount"
                    body={(rowData, props) => {
                      return (
                        <>
                          <span className="p-column-title text-break">Amount</span>
                          <span className="text-break">
                            {formatNumbersToCurrencyString(rowData.actual_amount ?? rowData.flat_fee_amount)}
                          </span>
                          {flatFeeError && flatFeeError[`${props.rowIndex}_amount`] && (
                            <span className="text-danger">{flatFeeError[`${props.rowIndex}_amount`]}</span>
                          )}
                        </>
                      );
                    }}
                    editor={(props) => {
                      const rowId = props?.rowData?.id;
                      return (
                        <div>
                          <InputText
                            type="text"
                            value={props.rowData?.actual_amount ?? props.rowData?.flat_fee_amount}
                            onFocus={() => {
                              const currentValue = props.rowData?.actual_amount ?? props.rowData?.flat_fee_amount;
                              if (!currentValue || Number(currentValue) === 0) {
                                updateRowById(rowId, {
                                  actual_amount: '',
                                  flat_fee_amount: '',
                                });
                              }
                            }}
                            onChange={(e) => {
                              const actualAmount = validateNumericInput(e.target.value);
                              const flatFeeAmount = caseDetails?.billing_preferences?.tax_percentage
                                ? addTax(e.target.value || 0, caseDetails?.billing_preferences?.tax_percentage || 0)
                                : fixToTwoDecimal(e.target.value);
                              updateRowById(rowId, {
                                actual_amount: actualAmount,
                                flat_fee_amount: flatFeeAmount,
                                tax_applied: false,
                              });
                              setFlatFeeError((prev) => {
                                const next = { ...prev };
                                delete next[`${props.rowIndex}_amount`];
                                return next;
                              });
                            }}
                            className="w-100 input-shadow"
                            maxLength={13}
                          />
                          {flatFeeError && flatFeeError[`${props.rowIndex}_amount`] && (
                            <span className="text-danger">{flatFeeError[`${props.rowIndex}_amount`]}</span>
                          )}
                        </div>
                      );
                    }}
                    footer={formatNumbersToCurrencyString(flatFeeActualTotal)}
                  />
                  {caseDetails?.billing_preferences?.amount_with_tax && (
                    <Column
                      header="Amount (with Tax)"
                      field="flat_fee_amount"
                      body={(rowData, props) => {
                        return (
                          <>
                            <span className="p-column-title text-break">Amount</span>
                            <span className="text-break">{formatNumbersToCurrencyString(rowData.flat_fee_amount)}</span>
                          </>
                        );
                      }}
                      footer={formatNumbersToCurrencyString(flatFeeTotal)}
                    />
                  )}
                  <Column
                    header="Billable"
                    body={(rowData, props) => {
                      return nonBillableFlatFeeBody(rowData, props);
                    }}
                  />
                  <Column
                    body={(rowData, props) => {
                      return (
                        <>
                          <span className="p-column-title text-break">Actions</span>
                          <div className="d-flex">
                            <i
                              className="icon-delete icon-red ms-2 mt-2 pointer"
                              onClick={(e) => {
                                deleteFlatFee(props);
                              }}
                            />
                          </div>
                        </>
                      );
                    }}
                  />
                </DataTable>

                {caseDetails?.billing_preferences?.tax_percentage && (
                  <div className="d-flex p-2">
                    <div className="col col-md-8 text-bold disclaimer">
                      Tax Rate @ {caseDetails?.billing_preferences?.tax_percentage || 0}%
                    </div>
                  </div>
                )}
                <div className="d-flex">
                  <div className="col-12 p-2">
                    <span className="addfield addfield-inline" role="button" onClick={addFlatFee}>
                      + Add Flat Fee
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FlatFeeBillingDetailsV2;
